import type { DashboardData } from '../types';
import type { UiLanguage } from './i18n';

async function fetchJson<T>(path: string, init: RequestInit = {}, timeoutMs = 15_000, externalSignal?: AbortSignal): Promise<T> {
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (externalSignal?.aborted) controller.abort();
  else externalSignal?.addEventListener('abort', abortFromCaller, { once: true });
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(path, { ...init, signal: controller.signal });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const detail = typeof payload?.error === 'string' ? payload.error : `${response.status} ${response.statusText}`;
      throw new Error(`HTTP ${response.status}: ${detail}`);
    }
    return payload as T;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      if (externalSignal?.aborted) throw Object.assign(new Error('Request cancelled.'), { name: 'AbortError' });
      throw new Error(`API request timed out after ${Math.round(timeoutMs / 1000)} seconds (${path}).`);
    }
    if (error instanceof TypeError) throw new Error(`Cannot reach ${path}. Check the dashboard API server or proxy, then retry.`);
    throw error;
  } finally {
    clearTimeout(timer);
    externalSignal?.removeEventListener('abort', abortFromCaller);
  }
}

let cachedDashboard: DashboardData | null = null;
let dashboardEtag: string | null = null;

const waitBeforeRetry = (attempt: number) => new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));

export async function fetchDashboard(): Promise<DashboardData> {
  let lastError = new Error('Dashboard API request failed.');
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12_000);
    const headers = new Headers({ accept: 'application/json' });
    if (dashboardEtag) headers.set('If-None-Match', dashboardEtag);
    try {
      const response = await fetch('/api/dashboard', { headers, cache: 'no-store', signal: controller.signal });
      if (response.status === 304 && cachedDashboard) return cachedDashboard;
      if (response.status === 304) {
        dashboardEtag = null;
        lastError = new Error('API returned 304 without a cached dashboard; retrying without a cache validator.');
      } else {
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const detail = typeof payload?.error === 'string' ? payload.error : `${response.status} ${response.statusText}`;
          const extra = typeof payload?.details === 'string' ? ` — ${payload.details.slice(0, 240)}` : '';
          lastError = new Error(`HTTP ${response.status}: ${detail}${extra}`);
          if (response.status < 500 || attempt === 2) throw lastError;
        } else {
          cachedDashboard = payload as DashboardData;
          dashboardEtag = response.headers.get('ETag');
          return cachedDashboard;
        }
      }
    } catch (error) {
      if (error === lastError) throw lastError;
      if (error instanceof Error && error.name === 'AbortError') lastError = new Error('Dashboard API request timed out after 12 seconds.');
      else if (error instanceof TypeError) lastError = new Error('Cannot reach /api/dashboard. Check the dashboard API server or proxy, then retry.');
      else throw error;
      if (attempt === 2) throw lastError;
    } finally {
      clearTimeout(timer);
    }
    await waitBeforeRetry(attempt);
  }
  throw lastError;
}

export async function requestRefresh(): Promise<void> {
  await fetchJson('/api/refresh', { method: 'POST', headers: { accept: 'application/json' } });
}

export async function requestLocalAi(): Promise<{ ok: boolean; updated?: number; error?: string }> {
  return fetchJson('/api/reclassify', { method: 'POST', headers: { accept: 'application/json' } });
}

export async function requestTranslation(title: string, summary: string, targetLanguage: string): Promise<{ ok: boolean; title: string; summary: string; language: string; model: string }> {
  return fetchJson('/api/translate', {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ title, summary, targetLanguage })
  }, 50_000);
}

export async function requestArticleSpeechTranslation(articleId: string, signal: AbortSignal): Promise<{ ok: boolean; text: string; language: string; cached: boolean }> {
  return fetchJson('/api/translate-audio', {
    method: 'POST',
    cache: 'no-store',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ articleId })
  }, 35_000, signal);
}

export function timeAgo(input?: string | null, language: UiLanguage = 'hi'): string {
  const english = language === 'en';
  if (!input) return english ? 'Not yet' : 'अभी तक नहीं';
  const timestamp = Date.parse(input);
  if (!Number.isFinite(timestamp)) return english ? 'Time unavailable' : 'समय अनुपलब्ध';
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return english ? 'Just now' : 'अभी';
  if (minutes < 60) return english ? `${minutes} min ago` : `${minutes} मिनट पहले`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return english ? `${hours} hr ago` : `${hours} घंटे पहले`;
  const days = Math.floor(hours / 24);
  return english ? `${days} days ago` : `${days} दिन पहले`;
}

export function formatClock(input?: string | null, language: UiLanguage = 'hi'): string {
  if (!input) return '—';
  const date = new Date(input);
  if (!Number.isFinite(date.getTime())) return '—';
  const locale = language === 'hi' ? 'hi-IN' : 'en-GB';
  return `${new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC', hour12: false }).format(date)} UTC`;
}


export async function requestUiTranslation(targetLanguage: string, strings: string[]): Promise<{ translations: string[]; language: string; model: string }> {
  return fetchJson('/api/translate-ui', {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ targetLanguage, strings })
  }, 100_000);
}
